import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentProps,
  type KeyboardEvent,
  type MouseEvent,
} from "react";
import { createPortal } from "react-dom";

type OptionItem = {
  value: string;
  label: string;
  disabled: boolean;
  group: string | null;
};

const viewportMargin = 8;
const popoverGap = 6;
const popoverMaxHeight = 320;

/** Список вариантов читаем прямо из `<select>`: он остаётся источником истины. */
function readOptions(select: HTMLSelectElement): OptionItem[] {
  return Array.from(select.options).map((option) => {
    const group = option.parentElement;
    return {
      value: option.value,
      label: option.text,
      disabled: option.disabled || (group instanceof HTMLOptGroupElement && group.disabled),
      group: group instanceof HTMLOptGroupElement ? group.label : null,
    };
  });
}

function nextEnabled(items: OptionItem[], from: number, step: 1 | -1): number {
  for (let index = from + step; index >= 0 && index < items.length; index += step) {
    if (!items[index]?.disabled) return index;
  }
  return from;
}

/**
 * Выбор значения из настоящего `<select>` через собственное меню.
 *
 * Системный список нельзя оформить и на узких экранах он вылезает за поле, а
 * все формы ERP строятся на `<select>` (значения, подписи, `FormData`, тесты).
 * Поэтому сам `<select>` остаётся в разметке и в фокусе, а вместо системного
 * меню по клику, Enter, пробелу и стрелкам открывается наше: оно не выходит за
 * границы окна, при нехватке места раскрывается вверх, а выбор отдаёт наружу
 * обычным событием `change`.
 */
export function Select({
  className,
  ref,
  onKeyDown,
  onMouseDown,
  onBlur,
  ...select
}: ComponentProps<"select">) {
  const triggerRef = useRef<HTMLSelectElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const typeahead = useRef({ text: "", at: 0 });
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<OptionItem[]>([]);
  const [active, setActive] = useState(-1);
  const [container, setContainer] = useState<HTMLElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLSelectElement | null) => {
      triggerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) ref.current = node;
    },
    [ref],
  );

  const close = useCallback(() => setOpen(false), []);

  const openList = useCallback(() => {
    const trigger = triggerRef.current;
    if (trigger === null || trigger.disabled) return;
    const options = readOptions(trigger);
    setItems(options);
    setActive(
      options.findIndex((item) => item.value === trigger.value && !item.disabled),
    );
    // Внутри модального `<dialog>` всё за его пределами недоступно, поэтому
    // меню кладём внутрь него, а иначе — в конец `body`.
    setContainer(trigger.closest("dialog") ?? document.body);
    setOpen(true);
  }, []);

  const choose = useCallback(
    (index: number) => {
      const trigger = triggerRef.current;
      const item = items[index];
      if (trigger === null || item === undefined || item.disabled) return;
      if (trigger.value !== item.value) {
        trigger.value = item.value;
        trigger.dispatchEvent(new Event("change", { bubbles: true }));
      }
      setOpen(false);
      trigger.focus();
    },
    [items],
  );

  // Положение считаем по окну, а не по родителю: меню не должно выходить за
  // границы экрана ни по ширине, ни по высоте.
  const place = useCallback(() => {
    const trigger = triggerRef.current;
    const popover = popoverRef.current;
    if (trigger === null || popover === null) return;
    const rect = trigger.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight;
    const width = Math.min(
      Math.max(rect.width, popover.scrollWidth),
      viewportWidth - viewportMargin * 2,
    );
    // Ширину задаём до замера высоты: от неё зависит перенос длинных названий.
    popover.style.width = `${width}px`;
    popover.style.maxHeight = `${popoverMaxHeight}px`;
    const below = viewportHeight - rect.bottom - viewportMargin - popoverGap;
    const above = rect.top - viewportMargin - popoverGap;
    const wanted = popover.offsetHeight;
    const openBelow = below >= wanted || below >= above;
    const room = Math.min(
      Math.max(openBelow ? below : above, 96),
      popoverMaxHeight,
    );
    const height = Math.min(wanted, room);
    const left = Math.min(
      Math.max(rect.left, viewportMargin),
      viewportWidth - width - viewportMargin,
    );
    popover.style.maxHeight = `${room}px`;
    popover.style.left = `${left}px`;
    popover.style.top = `${
      openBelow ? rect.bottom + popoverGap : rect.top - popoverGap - height
    }px`;
    popover.dataset.side = openBelow ? "below" : "above";
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    place();
    popoverRef.current
      ?.querySelector<HTMLElement>("[aria-selected='true']")
      ?.scrollIntoView?.({ block: "center" });
  }, [open, place, items]);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      )
        setOpen(false);
    };
    const scroll = (event: Event) => {
      if (popoverRef.current?.contains(event.target as Node)) return;
      const trigger = triggerRef.current;
      if (trigger === null) return;
      const rect = trigger.getBoundingClientRect();
      // Поле уехало из видимой области — меню без него теряет смысл.
      if (rect.bottom < 0 || rect.top > window.innerHeight) setOpen(false);
      else place();
    };
    document.addEventListener("pointerdown", outside, true);
    window.addEventListener("scroll", scroll, true);
    window.addEventListener("resize", place);
    return () => {
      document.removeEventListener("pointerdown", outside, true);
      window.removeEventListener("scroll", scroll, true);
      window.removeEventListener("resize", place);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open || active < 0) return;
    popoverRef.current
      ?.querySelector<HTMLElement>(`[data-index='${active}']`)
      ?.scrollIntoView?.({ block: "nearest" });
  }, [open, active]);

  const handleMouseDown = (event: MouseEvent<HTMLSelectElement>) => {
    onMouseDown?.(event);
    if (event.defaultPrevented || event.button !== 0) return;
    const trigger = event.currentTarget;
    if (trigger.disabled) return;
    // Без этого браузер откроет своё системное меню поверх нашего.
    event.preventDefault();
    trigger.focus();
    if (open) close();
    else openList();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLSelectElement>) => {
    onKeyDown?.(event);
    if (event.defaultPrevented) return;
    const { key } = event;
    if (!open) {
      if (
        key === "Enter" ||
        key === " " ||
        key === "ArrowDown" ||
        key === "ArrowUp"
      ) {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (key === "Escape") {
      // Иначе Escape заодно закрыл бы диалог, в котором стоит поле.
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (key === "Tab") {
      close();
    } else if (key === "ArrowDown") {
      event.preventDefault();
      setActive((index) => nextEnabled(items, index, 1));
    } else if (key === "ArrowUp") {
      event.preventDefault();
      setActive((index) => nextEnabled(items, index < 0 ? items.length : index, -1));
    } else if (key === "Home") {
      event.preventDefault();
      setActive(nextEnabled(items, -1, 1));
    } else if (key === "End") {
      event.preventDefault();
      setActive(nextEnabled(items, items.length, -1));
    } else if (key === "Enter" || key === " ") {
      event.preventDefault();
      choose(active);
    } else if (key.length === 1 && !event.ctrlKey && !event.metaKey) {
      // Поиск по первым буквам: список часовых поясов иначе не пролистать.
      const state = typeahead.current;
      const now = Date.now();
      state.text = now - state.at > 700 ? key : state.text + key;
      state.at = now;
      const needle = state.text.toLowerCase();
      const found = items.findIndex(
        (item) => !item.disabled && item.label.toLowerCase().startsWith(needle),
      );
      if (found >= 0) setActive(found);
    }
  };

  const activeId = open && active >= 0 ? `${listId}-${active}` : undefined;

  return (
    <>
      <select
        {...select}
        ref={setRefs}
        className={className ? `select-control ${className}` : "select-control"}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={activeId}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        onBlur={(event) => {
          onBlur?.(event);
          close();
        }}
      />
      {open && container !== null
        ? createPortal(
            <div
              ref={popoverRef}
              id={listId}
              className="select-popover"
              role="listbox"
              aria-label={select["aria-label"]}
            >
              {items.map((item, index) => {
                const selected = item.value === triggerRef.current?.value;
                const heading =
                  item.group !== null && item.group !== items[index - 1]?.group;
                return (
                  <div key={`${item.value}-${index}`} role="presentation">
                    {heading ? (
                      <div className="select-group" role="presentation">
                        {item.group}
                      </div>
                    ) : null}
                    <div
                      id={`${listId}-${index}`}
                      className="select-option"
                      role="option"
                      aria-selected={selected}
                      aria-disabled={item.disabled || undefined}
                      data-index={index}
                      data-active={index === active || undefined}
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseMove={() => {
                        if (!item.disabled && index !== active) setActive(index);
                      }}
                      onClick={() => choose(index)}
                    >
                      <span className="select-option__label">
                        {item.label === "" ? " " : item.label}
                      </span>
                      {selected ? (
                        <svg
                          className="select-option__check"
                          viewBox="0 0 16 16"
                          width="16"
                          height="16"
                          fill="none"
                          aria-hidden="true"
                        >
                          <path
                            d="M3.5 8.5l3 3 6-6.5"
                            stroke="currentColor"
                            strokeWidth="1.8"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>,
            container,
          )
        : null}
    </>
  );
}
